import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { Typography } from '@mui/material';
import { clearWavetrakIdentity, trackEvent, getWindow } from '@surfline/web-common';

import { doUpdateUserSettings } from 'actions/user';
import { CLICKED_SUBSCRIBE_CTA, WHATS_NEW_URL } from 'common/constants';
import WavetrakLink from 'components/WavetrakLink';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import { getIsSubscribed, getIsNonSubscribedWithAccess, getIsGuestUser } from 'selectors/user';
import { isPremiumPlusUser, isPremiumUser, getIsPremiumWithAdsUser } from 'utils/user';

import BaseMenu from '../BaseMenu';
import BaseMenuSection from '../BaseMenuSection';
import BaseMenuItem from '../BaseMenuItem';
import NewNotification from '../NewNotification';
import { getAccountLinks, homeLinks, promotionalLinks, productLinks } from '../../defaultLinks';

import styles from './UserMenu.module.scss';

interface UserMenuProps {
  user?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    isEmailVerified?: boolean;
  } | null;
  entitlements?: Array<string>;
  showHome?: boolean;
  showDetails?: boolean;
  onClickLink?: (clickProperties?: any, closeMobileMenu?: boolean) => void;
  freeTrialEligible?: boolean;
  accountWarning?: boolean;
  featureRelease?: { title: string; date: string; url?: string };
  premiumPerks?: { date: string; url?: string };
  showFeatureReleaseNotification?: boolean;
  showPremiumPerksNotification?: boolean;
  showManageMembersLink?: boolean;
}

const UserMenu = ({
  accountWarning = false,
  entitlements = [],
  freeTrialEligible = false,
  onClickLink,
  showDetails = false,
  showHome = false,
  user = null,
  featureRelease,
  premiumPerks,
  showFeatureReleaseNotification = false,
  showPremiumPerksNotification = false,
  showManageMembersLink = false,
}: UserMenuProps) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  let isSubscribed = useAppSelector(getIsSubscribed);
  const isNonSubscribedWithAccess = useAppSelector(getIsNonSubscribedWithAccess);
  const isGuestUser = useAppSelector(getIsGuestUser);
  if (isNonSubscribedWithAccess) {
    isSubscribed = false;
  }
  const isPremium = isPremiumUser(entitlements);
  const isPremiumPlus = isPremiumPlusUser(entitlements);
  const isPremiumWithAdsUser = getIsPremiumWithAdsUser(entitlements);

  const isForecastOnly = entitlements.includes('sl_forecast_only');
  const isFamilyPlanUser = entitlements.includes('sl_family');
  const isFamilyMember = isGuestUser && isPremium;
  const hasPremiumEntitlementOnly = (isPremium && !isPremiumPlus) || isForecastOnly;
  const showCta =
    isPremiumPlus || isFamilyPlanUser || isFamilyMember
      ? false
      : entitlements.includes('sl_forecast_only') || freeTrialEligible || hasPremiumEntitlementOnly;
  const upgradeText = isPremium ? 'Upgrade to Premium+ now' : 'Upgrade now';

  const upgradeLink = useMemo(() => {
    if (hasPremiumEntitlementOnly) return '/upgrade';
    if (isSubscribed) {
      return '/account/subscription';
    }
    return '/upgrade';
  }, [hasPremiumEntitlementOnly, isSubscribed]);

  const handleOnClickCTA = useCallback(() => {
    trackEvent(CLICKED_SUBSCRIBE_CTA, {
      location: 'Account dropdown',
      path: router.asPath,
      url: `${window?.location.host}${router.asPath}`,
    });
  }, [router.asPath]);

  const clearNotificationAndRedirect = (
    hasNotification: boolean,
    clearNotification: Function,
    buttonText: string,
    redirectUrl: string,
  ) => {
    if (hasNotification) clearNotification();
    trackEvent('Button Clicked', {
      buttonText,
      pageName: window?.location.href,
      uiElement: 'User menu',
    });

    setTimeout(() => {
      getWindow()?.location.assign(redirectUrl);
    }, 300);
  };

  const handleClickedFeatureRelease = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const redirectUrl = featureRelease?.url || WHATS_NEW_URL;
      clearNotificationAndRedirect(
        showFeatureReleaseNotification,
        () => dispatch(doUpdateUserSettings({ releaseAnnouncementDate: featureRelease?.date })),
        'New Release',
        redirectUrl,
      );
    },
    [dispatch, featureRelease, showFeatureReleaseNotification],
  );

  const handleClickedPremiumPerks = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const redirectUrl = premiumPerks?.url || 'http://surfline.com/lp/perks';
      clearNotificationAndRedirect(
        showPremiumPerksNotification,
        () => dispatch(doUpdateUserSettings({ premiumPerkDate: premiumPerks?.date })),
        'Premium Perks',
        redirectUrl,
      );
    },
    [dispatch, premiumPerks, showPremiumPerksNotification],
  );

  const handleSignOut = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    /**
     * resets Segment session
     * https://segment.com/docs/sources/website/analytics.js/#reset-logout
     */
    window?.analytics?.reset();
    /**
     * resets Braze session (v3 api)
     * https://js.appboycdn.com/web-sdk/latest/doc/modules/appboy.html#wipedata
     */
    window?.appboy?.wipeData();
    window?.Sprig?.('logoutUser');
    clearWavetrakIdentity();
    setTimeout(() => window.location.assign('/logout'), 300);
  };

  const premiumStatusText = useMemo(() => {
    if (isPremiumPlus) return 'Premium+ member';
    if (isFamilyPlanUser || (isGuestUser && isPremium)) return 'Premium Family member';
    if (isForecastOnly) return 'Forecast member';
    if (isPremiumWithAdsUser) return 'Premium with Ads';
    return 'Premium member';
  }, [
    isPremiumPlus,
    isFamilyPlanUser,
    isGuestUser,
    isPremium,
    isForecastOnly,
    isPremiumWithAdsUser,
  ]);

  const renderNotification = () => {
    if (accountWarning) {
      return (
        <WavetrakLink
          className={classNames(styles.notification, styles.notificationWarning)}
          href="/account/subscription"
          isExternal
        >
          <Typography component="span" variant="subtitle1">
            Update Payment Details
          </Typography>
        </WavetrakLink>
      );
    }
    if (isSubscribed) {
      return (
        <div
          className={classNames({
            [styles.premiumStatus]: true,
            [styles.premiumPlusStatus]: isPremiumPlus,
          })}
        >
          <Typography component="span" variant="subtitle1">
            {premiumStatusText}
          </Typography>
        </div>
      );
    }
    if (user && !user?.isEmailVerified) {
      return (
        <WavetrakLink
          className={classNames(styles.notification, 'quiver-user-menu__notification')}
          href="/account/edit-profile"
          isExternal
        >
          <Typography component="span" variant="subtitle1">
            Verify your email
          </Typography>
        </WavetrakLink>
      );
    }
    return null;
  };
  const accountLinks = getAccountLinks(showManageMembersLink);
  return (
    <BaseMenu position="right" testId="user-menu">
      {showHome ? <BaseMenuSection links={homeLinks} onClick={onClickLink} /> : null}
      {user && showDetails ? (
        <BaseMenuSection>
          <div
            className={classNames({
              [styles.userDetailsSectionPremiumPlus]: isPremiumPlus,
            })}
            data-testid="user-details-section"
          >
            <div className={classNames(styles.userName, 'quiver-user-menu__user-name')}>
              <Typography component="span" variant="body">
                {user.firstName}
              </Typography>{' '}
              <Typography component="span" variant="body">
                {user.lastName}
              </Typography>
            </div>
            <div className={classNames(styles.userEmail, 'quiver-user-menu__user-email')}>
              <Typography component="span" variant="caption1">
                {user.email}
              </Typography>
            </div>
            {renderNotification()}
          </div>
        </BaseMenuSection>
      ) : null}
      {showCta ? (
        <BaseMenuSection>
          <BaseMenuItem href={upgradeLink} isExternal onClick={handleOnClickCTA}>
            <div className={classNames(styles.cta)}>
              <Typography
                color="text.primary"
                component="span"
                data-testid={freeTrialEligible ? 'navbar-user-menu-trial' : undefined}
                variant="subtitle1"
              >
                {freeTrialEligible ? 'Try Premium free for 7 days' : upgradeText}
              </Typography>
            </div>
          </BaseMenuItem>
        </BaseMenuSection>
      ) : null}
      {user ? (
        <>
          <BaseMenuSection links={accountLinks} onClick={onClickLink} />
          {featureRelease ? (
            <BaseMenuSection>
              <BaseMenuItem href={WHATS_NEW_URL} onClick={handleClickedFeatureRelease} isExternal>
                <div className={styles.featureRelease}>
                  <div>{featureRelease?.title || "What's New"}</div>
                  {showFeatureReleaseNotification ? <NewNotification /> : null}
                </div>
              </BaseMenuItem>
            </BaseMenuSection>
          ) : null}
          {premiumPerks ? (
            <BaseMenuSection>
              <BaseMenuItem
                href="http://surfline.com/lp/perks"
                onClick={handleClickedPremiumPerks}
                isExternal
              >
                <div className={styles.premiumPerks}>
                  <div>Perks</div>
                  {showPremiumPerksNotification ? <NewNotification /> : null}
                </div>
              </BaseMenuItem>
            </BaseMenuSection>
          ) : null}
          <BaseMenuSection>
            <BaseMenuItem href="https://support.surfline.com/hc/en-us" isExternal>
              <div className={styles.premiumPerks}>
                <div>Help Center</div>
              </div>
            </BaseMenuItem>
          </BaseMenuSection>
          <BaseMenuSection links={promotionalLinks} onClick={onClickLink} />
          <BaseMenuSection links={productLinks} onClick={onClickLink} />
          <BaseMenuSection>
            <BaseMenuItem href="/logout" onClick={handleSignOut}>
              Sign Out
            </BaseMenuItem>
          </BaseMenuSection>
        </>
      ) : null}
    </BaseMenu>
  );
};

export default UserMenu;
